import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { isSupabaseError } from '../../../../constants/supabaseErrors';
import { Quote, QuoteCompaisonColumnDatabase } from '../../../../types';
import { useSaveQuote } from '../../api/useSaveQuote';
import { parseQuoteDate, parseQuoteNumber, toQuoteDate } from '../../utils/quoteValues';

type Props = {
  groupId: string;
  columns: QuoteCompaisonColumnDatabase[];
  quote?: Quote;
  onClose: () => void;
};

function buildSchema(columns: QuoteCompaisonColumnDatabase[]) {
  const numberColumnIds = columns.filter((column) => column.type === 'number').map((column) => column.id);

  return z.object({
    provider: z.string().trim().min(1, 'Indiquez le prestataire'),
    issuedAt: z.date().nullable(),
    expirationDate: z.date().nullable(),
    fileUrl: z.union([z.literal(''), z.url('Collez un lien complet, commençant par https://')]),
    values: z.record(z.string(), z.string()),
  }).superRefine((data, context) => {
    for (const columnId of numberColumnIds) {
      const value = data.values[columnId] ?? '';

      if (value.trim() !== '' && parseQuoteNumber(value) === null) {
        context.addIssue({
          code: 'custom',
          path: ['values', columnId],
          message: 'Saisissez un nombre, par exemple 1250,50',
        });
      }
    }

    if (data.issuedAt && data.expirationDate && data.expirationDate < data.issuedAt) {
      context.addIssue({
        code: 'custom',
        path: ['expirationDate'],
        message: 'La date d\'expiration doit suivre la date d\'émission',
      });
    }
  });
}

type FormValues = z.infer<ReturnType<typeof buildSchema>>;

export function useQuoteFormDialog(props: Props) {
  const { mutateAsync, isPending } = useSaveQuote();
  const [serverError, setServerError] = useState<string | null>(null);
  const isEdition = !!props.quote;
  const visibleColumns = props.columns.filter((column) => column.visible);
  const schema = useMemo(() => buildSchema(props.columns), [props.columns]);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      provider: props.quote?.provider ?? '',
      issuedAt: parseQuoteDate(props.quote?.issued_at),
      expirationDate: parseQuoteDate(props.quote?.expiration_date),
      fileUrl: props.quote?.file_url ?? '',
      values: Object.fromEntries(props.columns.map((column) => [
        column.id,
        String(props.quote?.values[column.id] ?? ''),
      ])),
    },
  });

  async function handleSubmit(values: FormValues) {
    setServerError(null);

    const normalizedValues = Object.fromEntries(props.columns.map((column) => {
      const value = (values.values[column.id] ?? '').trim();
      const number = column.type === 'number' ? parseQuoteNumber(value) : null;

      return [column.id, number === null ? value : String(number)];
    }));

    try {
      await mutateAsync({
        groupId: props.groupId,
        quoteId: props.quote?.id,
        provider: values.provider.trim(),
        fileUrl: values.fileUrl === '' ? null : values.fileUrl,
        issuedAt: toQuoteDate(values.issuedAt),
        expirationDate: toQuoteDate(values.expirationDate),
        values: normalizedValues,
      });
      props.onClose();
    } catch (error: unknown) {
      setServerError(isSupabaseError(error) ? error.message : 'Le devis n\'a pas pu être enregistré. Réessayez.');
    }
  }

  function handleClose() {
    if (!isPending) {
      props.onClose();
    }
  }

  return {
    form,
    isEdition,
    visibleColumns,
    onSubmit: form.handleSubmit(handleSubmit),
    isPending,
    serverError,
    handleClose,
  };
}
